import { CONDITION_LINK_PROTOCOLS } from './conditionLink';
import { preventionScreeningProtocol } from './definitions/preventionContext';
import { t2dmProtocol } from './definitions/t2dmContext';
import type { ProtocolSpec } from './planner/plan';

/**
 * Every protocol that can be applied by hand, keyed by the versioned canonical its instances record.
 * A caller names a protocol by that canonical rather than passing a spec, so the API surface carries
 * a string and the definition stays in this package.
 *
 * Every protocol is here, including the ones a condition link also applies. How a protocol's actions
 * reach the plan is decided by the caller, not by the protocol: a manual apply commits, because a
 * clinician picked it deliberately and expects to see the result, while a condition link suggests, so
 * nothing lands on a freshly bootstrapped chart unreviewed. The same definition serves both.
 */
export const PROTOCOL_SPECS: readonly ProtocolSpec[] = [
  t2dmProtocol,
  // Person-level: applied to every patient by the ensure step, and by hand from the care plan.
  preventionScreeningProtocol,
  ...CONDITION_LINK_PROTOCOLS.map((entry) => entry.spec),
];

/** What a protocol picker needs: the canonical to apply, and the words to show. */
export interface ProtocolSummary {
  canonical: string;
  title: string;
  version: string;
  description: string | undefined;
}

const canonicalOf = (spec: ProtocolSpec): string => `${spec.planDefinition.url}|${spec.planDefinition.version}`;

export const listProtocols = (): ProtocolSummary[] =>
  PROTOCOL_SPECS.map((spec) => ({
    canonical: canonicalOf(spec),
    title: spec.planDefinition.title ?? spec.planDefinition.name ?? canonicalOf(spec),
    version: spec.planDefinition.version ?? '',
    description: spec.planDefinition.description,
  }));

/**
 * The protocol a canonical names. Accepts the canonical with or without its version: an unversioned
 * url resolves to the one shipped version, so a caller holding a patient's older `instantiatesCanonical`
 * still re-applies against what we ship now (the apply rewrites the recorded version).
 */
export const findProtocol = (canonical: string): ProtocolSpec | undefined => {
  const exact = PROTOCOL_SPECS.find((spec) => canonicalOf(spec) === canonical);
  if (exact) return exact;
  const [url] = canonical.split('|');
  return PROTOCOL_SPECS.find((spec) => spec.planDefinition.url === url);
};
